import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Table,
  Spin,
  message,
  Select,
  Space,
  Typography,
  Tooltip,
} from "antd";
import { SwapOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { fetchBalanceSheet } from "../../api/accounting";
import useGlobalFilters from "../../../../hooks/useGlobalFilters";
import useDisplayCurrency from "../../../../hooks/useDisplayCurrency";
import { getCachedData, setCachedData, CacheTTL } from "../../context/AccountingDataCache";

const { RangePicker } = DatePicker;
const { Title } = Typography;

const BalanceSheet = () => {
  // Ensure quarter-based ranges behave correctly if we add local range rendering later
  dayjs.extend(quarterOfYear);
  const { selectedBrands, selectedClients } = useGlobalFilters();
  
  // Currency conversion
  const { displayCurrency, exchangeRate, isConverted } = useDisplayCurrency();
  
  // Currency formatter with conversion support
  const formatCurrency = useCallback((value) => {
    if (value === undefined || value === null) return "";
    
    const numValue = parseFloat(value || 0);
    const convertedValue = isConverted ? (numValue * exchangeRate) : numValue;
    
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: displayCurrency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertedValue);
    
    if (isConverted) {
      return (
        <Tooltip title={`Original: $${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (Rate: 1 USD = ${exchangeRate} ${displayCurrency})`}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            {formatted}
            <SwapOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
          </span>
        </Tooltip>
      );
    }
    
    return formatted;
  }, [isConverted, exchangeRate, displayCurrency]);

  const [dateRange, setDateRange] = useState([]);
  const [dateFilter, setDateFilter] = useState("this_month");
  const [showCustomRange, setShowCustomRange] = useState(false);

  // Create stable brandIds string to prevent infinite loops - directly from selectedBrands and selectedClients
  const brandIdsKey = useMemo(() => {
    const brandIdList = selectedBrands && selectedBrands.length > 0 
      ? selectedBrands.map((b) => b?.id || b).filter((id) => id).sort().join(",") 
      : "";
    const clientIdList = selectedClients && selectedClients.length > 0 
      ? selectedClients.map((c) => c?.id || c).filter((id) => id).sort().join(",") 
      : "";
    return `${brandIdList}__${clientIdList}`;
  }, [selectedBrands, selectedClients]);

  // Create stable dateRange string to prevent infinite loops
  const dateRangeKey = useMemo(() => {
    if (!dateRange || dateRange.length !== 2) return "";
    return `${dateRange[0].format("YYYY-MM-DD")}_${dateRange[1].format(
      "YYYY-MM-DD"
    )}`;
  }, [dateRange]);

  // Cache key
  const cacheKey = useMemo(() => {
    if (!brandIdsKey) return null;
    const dateKey = showCustomRange ? dateRangeKey : dateFilter;
    return `balance-sheet-${brandIdsKey}-${dateKey}`;
  }, [brandIdsKey, dateRangeKey, dateFilter, showCustomRange]);

  // Check global cache for initial data
  const initialCachedData = useMemo(() => {
    if (!cacheKey) return null;
    return getCachedData(cacheKey);
  }, [cacheKey]);

  const [loading, setLoading] = useState(() => !initialCachedData);
  const [balanceData, setBalanceData] = useState(() => initialCachedData?.data || null);
  const [asOf, setAsOf] = useState(() => initialCachedData?.asOf || null);

  const fetchingRef = useRef(false);
  const lastFetchedCacheKeyRef = useRef(initialCachedData ? cacheKey : null);

  const fetchData = async (force = false) => {
    // Explicitly require brands to be selected - no fallback
    if (!selectedBrands || selectedBrands.length === 0 || !cacheKey) {
      setBalanceData(null);
      setAsOf(null);
      return;
    }

    // Check GLOBAL cache first if not forcing refresh
    if (!force) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        setBalanceData(cachedData.data);
        setAsOf(cachedData.asOf);
        setLoading(false);
        lastFetchedCacheKeyRef.current = cacheKey;
        return;
      }
    }

    // Already fetched for this key
    if (!force && lastFetchedCacheKeyRef.current === cacheKey) {
      setLoading(false);
      return;
    }

    // Prevent duplicate fetches
    if (fetchingRef.current && !force) {
      return;
    }

    // Normalize brand IDs directly from selectedBrands
    const normalizedBrandIds = selectedBrands.map((b) => b?.id || b).filter((id) => id);
    if (normalizedBrandIds.length === 0) {
      setBalanceData(null);
      setAsOf(null);
      return;
    }

    // Extract client IDs directly from selectedClients
    const clientIdList = selectedClients && selectedClients.length > 0
      ? selectedClients.map((client) => client?.id || client).filter((id) => id)
      : [];

    fetchingRef.current = true;
    setLoading(true);

    try {
      const params = {
        brandIds: normalizedBrandIds,
        clientIds: clientIdList.length > 0 ? clientIdList : undefined,
        start:
          showCustomRange && dateRange?.[0]
            ? dayjs(dateRange[0]).format("YYYY-MM-DD")
            : undefined,
        end:
          showCustomRange && dateRange?.[1]
            ? dayjs(dateRange[1]).format("YYYY-MM-DD")
            : undefined,
        date_filter: !showCustomRange ? dateFilter : undefined,
      };
      const data = await fetchBalanceSheet(params);
      setBalanceData(data);
      setAsOf(data.as_of);

      // Cache in GLOBAL cache (5 minute TTL)
      setCachedData(cacheKey, { data, asOf: data.as_of }, CacheTTL.MEDIUM);
      lastFetchedCacheKeyRef.current = cacheKey;
    } catch (err) {
      message.error("Failed to fetch balance sheet data");
      setBalanceData(null);
      setAsOf(null);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  useEffect(() => {
    // Only fetch if brands are explicitly selected
    if (!selectedBrands || selectedBrands.length === 0 || !cacheKey) {
      setBalanceData(null);
      setAsOf(null);
      return;
    }

    // Check GLOBAL cache first - restore immediately if available
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      setBalanceData(cachedData.data);
      setAsOf(cachedData.asOf);
      setLoading(false);
      lastFetchedCacheKeyRef.current = cacheKey;
      return;
    }

    // Only fetch if not already fetched for this key
    if (lastFetchedCacheKeyRef.current !== cacheKey) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const columns = [
    {
      title: "Particulars",
      dataIndex: "name",
      key: "name",
    },
    {
      title: `Amount${isConverted ? ` (${displayCurrency})` : ''}`,
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (value) => formatCurrency(value),
    },
  ];

  // ---- Assets ----
  const assetRows = balanceData
    ? [
        ...(balanceData.assets?.accounts || []).map((account) => ({
          key: `asset_${account.account_id}`,
          name: `${account.code ? account.code + " - " : ""}${account.name}`,
          amount: account.balance,
        })),
        {
          key: "totalAssets",
          name: <strong>Total Assets</strong>,
          amount: balanceData.assets?.total_assets,
        },
      ]
    : [];

  // ---- Liabilities ----
  const liabilityRows = balanceData
    ? [
        ...(balanceData.liabilities?.accounts || []).map((account) => ({
          key: `liability_${account.account_id}`,
          name: `${account.code ? account.code + " - " : ""}${account.name}`,
          amount: account.balance,
        })),
        {
          key: "totalLiabilities",
          name: <strong>Total Liabilities</strong>,
          amount: balanceData.liabilities?.total_liabilities,
        },
      ]
    : [];

  // ---- Equity ----
  const equityRows = balanceData
    ? [
        ...(balanceData.equity?.accounts || []).map((account) => ({
          key: `equity_${account.account_id}`,
          name: `${account.code ? account.code + " - " : ""}${account.name}`,
          amount: account.balance,
        })),
        ...(balanceData.equity?.net_income &&
        balanceData.equity.net_income.balance !== 0
          ? [
              {
                key: "netIncome",
                name: "Net Income",
                amount: balanceData.equity.net_income.balance,
              },
            ]
          : []),
        {
          key: "totalEquity",
          name: <strong>Total Equity</strong>,
          amount: balanceData.equity?.total_equity,
        },
        {
          key: "totalLiabilitiesAndEquity",
          name: <strong>Total Liabilities & Equity</strong>,
          amount: balanceData?.total_liabilities_and_equity,
        },
      ]
    : [];

  const handleDateFilterChange = (value) => {
    if (value === "custom") {
      setShowCustomRange(true);
      setDateFilter(null);
    } else {
      setShowCustomRange(false);
      setDateFilter(value);
      setDateRange([]);
    }
  };

  return (
    <Row gutter={16}>
      <Col span={24}>
        <Card
          title={`Balance Sheet ${asOf ? `as of ${asOf}` : loading ? "Loading…" : ""}`}
          extra={
            <Space>
              <div
                style={{
                  padding: "4px 8px",
                  background: "#f0f0f0",
                  borderRadius: 4,
                }}
              >
                {selectedBrands.length > 0
                  ? `${selectedBrands.length} Brand(s) Selected`
                  : "No Brands Selected"}
              </div>
              <Select
                placeholder="Date Range"
                style={{ width: 180 }}
                value={showCustomRange ? "custom" : dateFilter}
                onChange={handleDateFilterChange}
              >
                <Select.Option value="this_month">This Month</Select.Option>
                <Select.Option value="this_quarter">
                  This Quarter
                </Select.Option>
                <Select.Option value="this_year">This Year</Select.Option>
                <Select.Option value="last_year">Last Year</Select.Option>
                <Select.Option value="custom">Custom Range</Select.Option>
              </Select>
              {showCustomRange && (
                <RangePicker
                  value={dateRange}
                  onChange={(values) => setDateRange(values)}
                />
              )}
              <Button type="primary" onClick={() => fetchData(true)} disabled={loading}>
                Apply
              </Button>
            </Space>
          }
        >
          <Spin spinning={loading} tip="Loading balance sheet…">
            <Row gutter={32}>
              <Col span={12}>
                <Title level={4}>Assets</Title>
                <Table
                  columns={columns}
                  dataSource={assetRows}
                  pagination={false}
                  size="small"
                  bordered
                />
              </Col>
              <Col span={12}>
                <Title level={4}>Liabilities</Title>
                <Table
                  columns={columns}
                  dataSource={liabilityRows}
                  pagination={false}
                  size="small"
                  bordered
                />
                <Title level={4} style={{ marginTop: 24 }}>
                  Equity
                </Title>
                <Table
                  columns={columns}
                  dataSource={equityRows}
                  pagination={false}
                  size="small"
                  bordered
                />
              </Col>
            </Row>
          </Spin>
        </Card>
      </Col>
    </Row>
  );
};

export default BalanceSheet;
